import React, { useEffect, useState, useRef } from "react";
import { useFormikContext, getIn } from "formik";

const CustomAutoCompleteCartaPorte = ({
  formik,
  field,
  label,
  searchFunction,
  width = "100%",
  required = true,
  descriptionField = "descripcion",
  idDisplayFormatter, // función opcional para cambiar lo que aparece entre ()
  useFormikPath = false,
  hideLabel = false,
}) => {
  const [value, setValue] = useState("");
  const [options, setOptions] = useState([]);
  const [showOptions, setShowOptions] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const timeoutIdRef = useRef(null);
  // const formik = useFormikContext();
  const error =
    formik?.touched &&
    formik?.errors &&
    getIn(formik.touched, field) &&
    getIn(formik.errors, field);

  // Genera el texto entre paréntesis usando idDisplayFormatter si existe
  const getIdDisplay = (id, item) => {
    return idDisplayFormatter ? idDisplayFormatter(id, item) : id;
  };

  const formatOptionText = (item) => {
    const idDisplay = getIdDisplay(item?.id, item);
    const description = item?.[descriptionField] ?? "";

    // Evitar duplicados cuando id y description son iguales
    if (idDisplay === description) {
      return `${idDisplay}`;
    }
    return `(${idDisplay}) ${description}`;
  };

  useEffect(() => {
    const selected = useFormikPath
      ? getIn(formik?.values, field)
      : formik?.values?.[field];

    if (selected) {
      setValue(selected);
      setText(formatOptionText(selected));
    } else {
      setValue(null);
      setText("");
    }
  }, [
    JSON.stringify(useFormikPath ? getIn(formik.values, field) : formik.values[field]),
    descriptionField,
    idDisplayFormatter,
  ]);

  useEffect(() => {
    const selected = useFormikPath
      ? getIn(formik?.values, field)
      : formik?.values?.[field];
    if (!selected) {
      setText(""); // Borra el texto visible
      setValue(null);
      setOptions([]);
    }
  }, [
    useFormikPath ? getIn(formik.values, field) : formik.values[field],
    field,
  ]);
  
  const handleChange = (event) => {
    const inputValue = event.target.value;
    setText(inputValue);

    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    if (inputValue.trim() === "") {
      setOptions([]);
      setValue(null);
      formik.setFieldValue(field, null);
      setLoading(false);
      return;
    }

    setLoading(true);
    timeoutIdRef.current = setTimeout(async () => {
      try {
        const data = await searchFunction(inputValue);
        setOptions(data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  const handleSelect = async (data) => {
    //console.log(`[CustomAutoComplete] Selección en campo "${field}":`, data);

    setValue(data);
    setText(formatOptionText(data));
    setShowOptions(false);
    await formik?.setFieldValue(field, data); // esperar a que cambie el valor
    formik?.setFieldTouched(field, true, true);
    formik?.validateField(field);
  };

  const clearSelection = () => {
    setText("");
    setValue(null);
    setOptions([]);
    setShowOptions(false);
    formik.setFieldValue(field, null);
  };

  const handleFocus = async () => {
    setShowOptions(true);
    if (text.trim() === "" && options.length === 0) {
      setLoading(true);
      try {
        const data = await searchFunction("");
        setOptions(data);
      } catch (error) {
        console.error("Error al cargar opciones:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowOptions(false), 150);
  };

  useEffect(() => {
    const initial = useFormikPath
      ? getIn(formik.values, field)
      : formik.values[field];
    if (initial && initial.id && !value?.id) {
      const fetchById = async () => {
        try {
          const results = await searchFunction(initial.id);
          const matched = results?.find((item) => item.id === initial.id);
          if (matched) {
            setValue(matched);
            setText(formatOptionText(matched));
            formik.setFieldValue(field, matched);
          }
        } catch (error) {
          console.error("Error buscando valor inicial:", error);
        }
      };
      fetchById();
    }
  }, [
    useFormikPath ? getIn(formik.values, field) : formik.values[field],
    field,
    descriptionField,
    idDisplayFormatter,
  ]);

  return (
    <div style={{ width }}>
      <div className="form-group">
      {!hideLabel && (
        <label
          className="form-label"
          style={{ color: error ? "red" : "inherit", display: "block" }}
        >
          {label}
          {required && (
            <span style={{ color: error ? "red" : "inherit" }}> *</span>
          )}
        </label>
      )}
        <div style={{ position: "relative" }}>
          {loading && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "10px",
                transform: "translateY(-50%)",
                zIndex: 2,
              }}
            >
              <span
                className="spinner-border spinner-border-sm text-primary"
                role="status"
              />
            </div>
          )}

          {text && (
            <button
              type="button"
              onClick={clearSelection}
              style={{
                position: "absolute",
                right: "27px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                color: "#999",
                cursor: "pointer",
                zIndex: 2,
                padding: 0,
                lineHeight: 1,
              }}
            >
              <i className="fa fa-times" />
            </button>
          )}

          <input
            type="text"
            className={`form-control ${error ? "is-invalid" : ""}`}
            placeholder={`Buscar ${label}`}
            value={text}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            autoComplete="password"
            style={{
              paddingLeft: loading ? "2rem" : undefined,
              paddingRight: text ? "2rem" : undefined,
            }}
          />

          {options.length > 0 && showOptions && (
            <ul
              className="list-group mt-2"
              style={{
                position: "absolute",
                zIndex: 1000,
                backgroundColor: "white",
                width: "100%",
                maxHeight: "200px",
                overflowY: "auto",
                border: "1px solid #ccc",
              }}
            >
              {options.map((data, index) => (
                <li
                  key={index}
                  className="list-group-item list-group-item-action"
                  onMouseDown={() => handleSelect(data)}
                >
                  {data.id === data[descriptionField]
                    ? `${data[descriptionField]}`
                    : `(${getIdDisplay(data.id, data)}) ${
                        data?.[descriptionField]
                      }`}
                </li>
              ))}
            </ul>
          )}

          {options.length === 0 && showOptions && !loading && (
            <ul
              className="list-group"
              style={{
                position: "absolute",
                zIndex: 1000,
                backgroundColor: "white",
                width: "100%",
                maxHeight: "200px",
                overflowY: "auto",
                border: "1px solid #ccc",
                top: "100%",
                marginTop: "0.25rem",
              }}
            >
              <li className="list-group-item list-group-item-action">
                No hay resultados
              </li>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomAutoCompleteCartaPorte;
